import Script from 'next/script';
import { memo, useCallback, useEffect, useState } from 'react';

import { reportErrorToBugsnag } from '@air/utils-error';

import {
  DEFAULT_PIXEL_PUBLIC_KEY,
  DEFAULT_PIXEL_SDK_READY_EVENT,
  DEFAULT_PIXEL_SDK_SCRIPT_URL,
  isDefaultPixelSdkConfigured,
} from '~/constants/defaultPixel';
import { ignoreAutomaticDefaultPixelFormCapture } from '~/utils/default-pixel/ignoreAutomaticDefaultPixelFormCapture';

const SCRIPT_ELEMENT_ID = 'default-pixel-sdk-script';

const notifyDefaultPixelSdkReady = () => {
  window.dispatchEvent(new CustomEvent(DEFAULT_PIXEL_SDK_READY_EVENT));
};

/**
 * Loads the Default Forms SDK when the demo modal opens.
 * The script tag stays after the modal closes. `data-default-ignore` stays on the
 * document for the rest of the session so other native forms are not captured.
 * Scheduler origins are left to the SDK default (`https://book.default.com`).
 */
export const DefaultPixelSdkScript = memo(() => {
  const [bootstrapReady, setBootstrapReady] = useState(false);

  useEffect(() => {
    if (!DEFAULT_PIXEL_PUBLIC_KEY) {
      return;
    }

    ignoreAutomaticDefaultPixelFormCapture();

    window.__defaultPixel__ = {
      key: DEFAULT_PIXEL_PUBLIC_KEY,
    };
    setBootstrapReady(true);
  }, []);

  const handleScriptLoad = useCallback(() => {
    if (DEFAULT_PIXEL_PUBLIC_KEY) {
      window.DefaultPixelSDK?.init({
        publicKey: DEFAULT_PIXEL_PUBLIC_KEY,
      });
    }

    window.__defaultPixelSdkStatus__ = 'loaded';
    notifyDefaultPixelSdkReady();
  }, []);

  const handleScriptError = useCallback(() => {
    reportErrorToBugsnag({
      error: new Error('Failed to load Default Pixel SDK script'),
      context: 'Failed to initialize Default Pixel SDK',
      metadata: {
        data: {
          scriptUrl: DEFAULT_PIXEL_SDK_SCRIPT_URL,
        },
      },
    });
    window.__defaultPixelSdkStatus__ = 'failed';
    notifyDefaultPixelSdkReady();
  }, []);

  if (!isDefaultPixelSdkConfigured() || !bootstrapReady) {
    return null;
  }

  return (
    <Script
      id={SCRIPT_ELEMENT_ID}
      src={DEFAULT_PIXEL_SDK_SCRIPT_URL}
      strategy="afterInteractive"
      onLoad={handleScriptLoad}
      onError={handleScriptError}
    />
  );
});

DefaultPixelSdkScript.displayName = 'DefaultPixelSdkScript';
